import React, { useMemo } from "react";
import dayjs from "dayjs";
import { Row, Col } from "antd";
import { getDashboardDates, formatNumber } from "../../utils";
import {
  DATALAYER_CHART_INFO,
  DATALAYER_MONITORED_INFO,
  DATALAYER_RECENT_ERRORS_INFO,
  DATALAYER_TOP_ERRORS_INFO,
} from "../../tooltips";
import { isStepFailure } from "../../../../utils/dataLayerGuard";
import SectionHeader from "../SectionHeader";
import HomeCard from "../HomeCard";
import InfoTooltip from "../InfoTooltip";
import DataLayerGuardChart from "../charts/DataLayerGuardChart";
import HomeTable from "../HomeTable";
import "./styles.scss";

const DASHBOARD_LINK = "/datalayer_guard_dashboard";

// Current hour in the client's timezone, "today" in the backend is the client's day
const clientHour = (timeZone) => {
  try {
    return Number(new Intl.DateTimeFormat("en-GB", { timeZone, hour: "2-digit", hourCycle: "h23" }).format(new Date()));
  } catch {
    return dayjs().hour();
  }
};

const ERROR_COLUMNS = [
  { key: "event_name", title: "Event name", width: "30%" },
  { key: "market", title: "Market", width: "25%" },
  { key: "error", title: "Error" },
];

/**
 * DataLayerGuardSection Component - DataLayer Guard section of the homepage
 *
 * @param {object} period - Period of the date filter, see getPeriod on the homepage
 * @param {object|null|undefined} context - DataLayer Guard data of the period, loaded once on the homepage with
 *   useDataLayerGuardData: undefined while loading and null when the request failed
 * @param {{events: number, errors: number}} totals - Events and errors of the period, the same as the error rate uses
 */
const DataLayerGuardSection = ({ period, context, totals }) => {
  const loading = context === undefined;
  const loadFailed = context === null;

  const errorsTimeline = context?.errors_timeline;
  const totalEventsTimeline = context?.total_events_timeline;
  const clientTimeZone = context?.BQ_client_timestamp;

  const timelineData = useMemo(() => {
    // Merge error counts into the total events timeline, so events without errors are shown too
    const errorsMap = {};
    (errorsTimeline || []).forEach((item) => {
      errorsMap[`${item.event_name}_${item.date}`] = item.error_count || 0;
    });

    // Per hour (today, yesterday) the chart only shows the time
    const toTimestamp = (date) => (period.window ? dayjs(date).format("HH:mm") : date);
    const rows = (totalEventsTimeline || []).map((item) => ({
      timestamp: toTimestamp(item.date),
      event_name: item.event_name || "Unknown Event",
      error_count: errorsMap[`${item.event_name}_${item.date}`] || 0,
    }));

    // Errors of events without snapshots (e.g. the network checks) are counted too, so they need their own points
    const eventDates = new Set((totalEventsTimeline || []).map((item) => `${item.event_name}_${item.date}`));
    (errorsTimeline || [])
      .filter((item) => !eventDates.has(`${item.event_name}_${item.date}`))
      .forEach((item) =>
        rows.push({
          timestamp: toTimestamp(item.date),
          event_name: item.event_name || "Unknown Event",
          error_count: item.error_count || 0,
        }),
      );

    // Per hour every hour of the day (today until now) gets a point, otherwise errors in one hour draw no line
    if (period.window && rows.length) {
      const lastHour = period.window === "today" ? clientHour(clientTimeZone) : 23;
      const hours = new Set(rows.map((row) => row.timestamp));
      for (let hour = 0; hour <= lastHour; hour++) {
        const timestamp = `${String(hour).padStart(2, "0")}:00`;
        if (!hours.has(timestamp)) rows.push({ timestamp, event_name: rows[0].event_name, error_count: 0 });
      }
    }
    return rows;
  }, [errorsTimeline, totalEventsTimeline, clientTimeZone, period]);

  // Successfully monitored events = total events - error events, never below 0
  const recentErrors = totals.errors;
  const monitoredEvents = Math.max(totals.events - recentErrors, 0);

  // Top 5 errors, without step failures like the client sees on the DataLayer Guard dashboard
  const encounteredErrors = (context?.errors || [])
    .filter((error) => !isStepFailure(error))
    .sort((a, b) => (b.occurrence_count || 0) - (a.occurrence_count || 0))
    .slice(0, 5);

  return (
    <section className="datalayer-guard-section">
      <SectionHeader
        title="DataLayer Guard"
        color="lime"
        configureLink="/datalayer_guard_config"
      />

      <Row gutter={[16, 16]}>
        {/* The Recent errors card lines up with the Recent errors card of Tag Monitor above */}
        <Col xs={24} lg={20}>
          <div className="monitored-banner">
            <span className="monitored-banner-value">
              {loading || loadFailed ? "-" : formatNumber(monitoredEvents)}
            </span>
            <span className="monitored-banner-text">
              Successfully monitored dataLayer events
              {/* <InfoTooltip text={DATALAYER_MONITORED_INFO} /> */}
            </span>
            <span className="monitored-banner-circle" />
          </div>
        </Col>
        <Col xs={24} lg={4}>
          <HomeCard className="stat-card">
            <span className="stat-card-label">
              Recent errors
              {/* <InfoTooltip text={DATALAYER_RECENT_ERRORS_INFO} /> */}
            </span>
            <span className="stat-card-value stat-card-value-error">
              {loading || loadFailed ? "-" : formatNumber(recentErrors)}
            </span>
          </HomeCard>
        </Col>
      </Row>

      <Row gutter={[16, 16]}>
        {/* Half width, so the panels line up with the Tag Monitor panels above */}
        <Col xs={24} lg={12}>
          <HomeCard
            title="Event errors"
            // info={DATALAYER_CHART_INFO}
            viewMore
            viewMoreLink={`${DASHBOARD_LINK}?${getDashboardDates(period)}`}
          >
            <DataLayerGuardChart timelineData={timelineData} title="" loading={loading} />
          </HomeCard>
        </Col>
        <Col xs={24} lg={12}>
          <HomeCard
            title="Top 5 recent errors"
            // info={DATALAYER_TOP_ERRORS_INFO}
            viewMore
            viewMoreLink={`${DASHBOARD_LINK}?${getDashboardDates(period)}`}
          >
            <HomeTable
              columns={ERROR_COLUMNS}
              rows={encounteredErrors.map((error, index) => ({
                key: index,
                event_name: error.event_name || "Unknown Event",
                market: error.market || error.hostname || "-",
                error: error.description || error.error_description || "Unknown Error",
              }))}
              loading={loading}
              emptyMessage={
                loadFailed ? "DataLayer Guard data could not be loaded." : "No DataLayer Guard errors detected."
              }
            />
          </HomeCard>
        </Col>
      </Row>
    </section>
  );
};

export default DataLayerGuardSection;
